import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * GroupAssignDrawer — Assign groups to a window or careflow.
 *
 * Props:
 *   open          — drawer visibility
 *   onClose       — close callback
 *   onSave        — save callback (targetId, selectedGroupIds[], timingData?)
 *                    timingData is { eventTime, sendNextMessage, sendMax } for mobile/pager
 *   saving        — boolean
 *   windowTarget  — the window/careflow object being edited
 *   groups        — available groups array
 *   groupType     — 'resident' | 'area' | 'mobile' | 'pager'
 *   initialIds    — optional array of pre-selected ID strings (for careflow targets)
 *   showEnabledToggle — opt-in On/Off switch for the whole output. OFF by default because
 *                    this drawer serves three call sites: careflow mobile/pager outputs
 *                    (which have a master enabled flag), careflow reference groups, and
 *                    check-in/rounds window groups. Only the first has anything to toggle.
 *   canToggleOutput — when false the switch is hidden even if showEnabledToggle is set
 *                    (low-privilege users may not turn outputs off).
 */

const GROUP_CONFIG = {
    resident: { idKey: 'resident_group_id', nameKey: 'resident_group_name', descKey: 'resident_description', title: 'Assign Resident Groups', label: 'resident' },
    area:     { idKey: 'area_group_id',     nameKey: 'area_group_name',     descKey: 'area_group_description', title: 'Assign Area Groups', label: 'area' },
    mobile:   { idKey: 'group_id',          nameKey: 'group_name',          descKey: '',                       title: 'Assign Mobile Groups', label: 'mobile' },
    pager:    { idKey: 'group_id',          nameKey: 'group_name',          descKey: 'capcode',                title: 'Assign Pager Groups', label: 'pager' },
};

const GroupAssignDrawer = ({ open, onClose, onSave, saving, windowTarget, groups, groupType, initialIds,
    showEnabledToggle = false, canToggleOutput = true }) => {
    const cfg = GROUP_CONFIG[groupType] || GROUP_CONFIG.resident;
    const { idKey, nameKey, descKey } = cfg;
    const hasTiming = groupType === 'mobile' || groupType === 'pager';
    const isPager = groupType === 'pager';

    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    // Timing fields (mobile/pager only)
    const [messageTime, setMessageTime] = useState(0);
    const [repeatEnabled, setRepeatEnabled] = useState(false);
    /* Defaults ON each open, including for an output that is currently off — reaching
       this drawer from an Off badge has always meant "configure and enable". */
    const [outputEnabled, setOutputEnabled] = useState(true);
    const [repeatInterval, setRepeatInterval] = useState(5);

    // Initialize selected IDs from window target when drawer opens
    useEffect(() => {
        if (open && windowTarget) {
            let ids;
            if (initialIds != null) {
                // Careflow targets pass initial IDs directly
                ids = Array.isArray(initialIds) ? initialIds : [];
            } else if (groupType === 'resident') {
                const rawIds = windowTarget.resident_group_id_list;
                ids = rawIds && rawIds !== '' ? rawIds.split(',') : [];
            } else if (groupType === 'area') {
                const rawIds = windowTarget.area_ids;
                ids = rawIds && rawIds !== '' ? rawIds.split(',') : [];
            } else {
                ids = [];
            }
            setSelectedIds(ids);
            setOutputEnabled(true);
            setSearch('');
            setShowSelectedOnly(false);

            // Initialize timing fields for mobile/pager
            if (hasTiming && windowTarget.outputs) {
                const outputKey = groupType === 'mobile' ? 'palmobile' : 'pager';
                const output = windowTarget.outputs[outputKey] || {};

                if (isPager) {
                    // Pager event_time is stored in seconds
                    setMessageTime(output.event_time || 0);
                } else {
                    // Mobile event_time is stored in seconds, display as minutes
                    setMessageTime(output.event_time ? Math.round(output.event_time / 60) : 0);
                }
                // Repeat toggle tracks the explicit `repeat_enabled` flag
                // returned by the backend. The interval value comes from
                // `next_event_time` (seconds) — display it when present so
                // the field shows the stored minutes even if the toggle
                // happens to be off.
                setRepeatEnabled(!!output.repeat_enabled);
                const intervalSec = parseInt(output.next_event_time, 10);
                const hasInterval = Number.isFinite(intervalSec) && intervalSec > 0;
                setRepeatInterval(hasInterval ? Math.round(intervalSec / 60) : 5);
            }
        }
    }, [open, windowTarget, groupType, initialIds, hasTiming, isPager]);

    // Filter and sort groups
    const filteredGroups = useMemo(() => {
        let list = [...(groups || [])];
        if (showSelectedOnly) {
            list = list.filter((g) => selectedIds.includes(String(g[idKey])));
        }
        if (search.trim()) {
            const term = search.toLowerCase();
            list = list.filter(
                (g) =>
                    (g[nameKey] || '').toLowerCase().includes(term) ||
                    (g[descKey] || '').toLowerCase().includes(term)
            );
        }
        list.sort((a, b) =>
            (a[nameKey] || '').localeCompare(b[nameKey] || '', undefined, { sensitivity: 'base' })
        );
        return list;
    }, [groups, search, showSelectedOnly, selectedIds, nameKey, descKey, idKey]);

    const handleToggle = useCallback((groupId) => {
        const strId = String(groupId);
        setSelectedIds((prev) =>
            prev.includes(strId) ? prev.filter((id) => id !== strId) : [...prev, strId]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        const allFilteredIds = filteredGroups.map((g) => String(g[idKey]));
        setSelectedIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
    }, [filteredGroups, idKey]);

    const handleClear = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const handleSave = () => {
        const targetId = windowTarget?.window_id || windowTarget?.careflow_id;
        if (hasTiming) {
            const timingData = {
                eventTime: isPager ? parseInt(messageTime) : parseInt(messageTime) * 60,
                sendNextMessage: repeatEnabled ? parseInt(repeatInterval) * 60 : 0,
                sendMax: repeatEnabled,
                /* Intent only — WellnessSettingsPage owns what turning an output off
                   clears, so all three output drawers behave identically. */
                enabled: showEnabledToggle ? outputEnabled : true,
            };
            onSave(targetId, selectedIds, timingData);
        } else {
            onSave(targetId, selectedIds);
        }
    };

    const title = cfg.title;

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">{title}</h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {windowTarget ? (
                        <p style={{ marginBottom: 'var(--space-3, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                            {windowTarget.careflow_description ? 'Careflow' : 'Window'}: <strong>{windowTarget.careflow_description || windowTarget.description || '(unnamed)'}</strong>
                        </p>
                    ) : null}

                    {/* Timing fields for mobile/pager */}
                    {showEnabledToggle && canToggleOutput ? (
                        <div className="dp-drawer__field">
                            <div className="ws-toggle-row">
                                <span className="ws-toggle-label">Output Enabled</span>
                                <label className="dp-toggle">
                                    <input
                                        type="checkbox"
                                        checked={outputEnabled}
                                        onChange={(e) => setOutputEnabled(e.target.checked)}
                                    />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!outputEnabled ? (
                                <p className="dp-drawer__error">
                                    Saving will turn this output off and disconnect all groups. Timing settings are kept.
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    {hasTiming ? (
                        <>
                            <div className="dp-drawer__field">
                                <label className="dp-drawer__label">
                                    {isPager ? 'Message After (seconds)' : 'Message After (minutes)'}
                                </label>
                                <input
                                    className="dp-drawer__input"
                                    type="number"
                                    min="0"
                                    max={isPager ? 7200 : 120}
                                    value={messageTime}
                                    onChange={(e) => setMessageTime(e.target.value)}
                                />
                                <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                                    0 = Immediate Send. {isPager ? '1–7200 = Seconds' : '1–120 = Minutes'} from alarm generation when notification will send.
                                </p>
                            </div>

                            <div className="ws-toggle-row">
                                <span className="ws-toggle-label">Send Repeats</span>
                                <label className="dp-toggle">
                                    <input
                                        type="checkbox"
                                        checked={repeatEnabled}
                                        onChange={(e) => setRepeatEnabled(e.target.checked)}
                                    />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>

                            {repeatEnabled ? (
                                <div className="dp-drawer__field">
                                    <label className="dp-drawer__label">Repeat Interval (minutes)</label>
                                    <input
                                        className="dp-drawer__input"
                                        type="number"
                                        min="2"
                                        max="60"
                                        value={repeatInterval}
                                        onChange={(e) => setRepeatInterval(e.target.value)}
                                    />
                                    <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                                        Set number of minutes {isPager ? 'pager' : 'mobile'} will repeat notification.
                                    </p>
                                </div>
                            ) : null}

                            <div style={{ borderTop: '1px solid var(--color-border, #e5e7eb)', margin: 'var(--space-3, 0.75rem) 0' }} />
                        </>
                    ) : null}

                    {/* Picker */}
                    <div className="ws-device-picker">
                        <div className="ws-device-picker__header">
                            <p className="dp-drawer__label" style={{ marginBottom: 0 }}>
                                Groups
                                <span className="ws-device-picker__count">
                                    ({selectedIds.length} selected)
                                </span>
                            </p>
                        </div>

                        {/* Search */}
                        <div className="ws-device-picker__search">
                            <i className="fe fe-search ws-device-picker__search-icon" aria-hidden="true" />
                            <input
                                type="text"
                                placeholder={`Search ${cfg.label} groups...`}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="ws-device-picker__search-input"
                            />
                        </div>

                        {/* Select All / Clear / Show Selected */}
                        <div className="ws-device-picker__actions">
                            <button
                                type="button"
                                onClick={handleSelectAll}
                                className="ws-device-picker__action-btn"
                            >
                                Select All
                            </button>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="ws-device-picker__action-btn"
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowSelectedOnly((prev) => !prev)}
                                className={`ws-device-picker__action-btn${showSelectedOnly ? ' ws-device-picker__action-btn--active' : ''}`}
                            >
                                Show Selected
                            </button>
                        </div>

                        {/* Group list */}
                        <div className="ws-device-picker__list">
                            {filteredGroups.length === 0 ? (
                                <p className="ws-device-picker__empty">
                                    {showSelectedOnly ? 'No selections.' : `No ${cfg.label} groups found`}
                                </p>
                            ) : (
                                filteredGroups.map((g) => {
                                    const gId = String(g[idKey]);
                                    const isChecked = selectedIds.includes(gId);
                                    return (
                                        <label
                                            key={gId}
                                            className={`ws-device-picker__item${isChecked ? ' is-selected' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleToggle(g[idKey])}
                                                className="ap-checkbox"
                                            />
                                            <div className="ws-device-picker__item-info">
                                                <span className="ws-device-picker__item-name">{g[nameKey] || '—'}</span>
                                                {g[descKey] ? (
                                                    <span className="ws-device-picker__item-desc">{g[descKey]}</span>
                                                ) : null}
                                            </div>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                        {saving ? 'Saving...' : 'Save Groups'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default GroupAssignDrawer;
